import { mapGetters, mapState } from 'vuex';
import get from 'lodash/get';
import pick from 'lodash/pick';
import { alphabetSort } from '@/libs';
import { SIDE_PANEL_TAB_NAMES, AGENT_PROFILE_MAPPING } from '@/constants';
import { PROFILE_EDITOR_TYPE } from '@/constants/settings';
import { SIDE_PANEL_PREFIX, CUSTOMER_INFO } from '@/constants/analytics';
import {
  ConversationMetaList,
  KnowledgeBase,
  History,
  RemindersList,
  CustomerInfo,
  MyToolBox,
  ConversationReviewsList,
  CustomNotes,
  Campaign,
} from './children';
import isEmpty from 'lodash/isEmpty';
import capitalize from 'lodash/capitalize';
import toUpper from 'lodash/toUpper';
import toLower from 'lodash/toLower';

const SIDE_PANEL_MAX_DROPBOX_ITEMS = 1000;
const TRANSFORMATIONS_MAP = new Map([
  ['capitalize', (str) => str.split(' ').map(word => capitalize(word)).join(' ')],
  ['uppercase', toUpper],
  ['lowercase', toLower],
]);

export default {
  name: 'sidePanel',
  components: {
    ConversationMetaList,
    KnowledgeBase,
    History,
    RemindersList,
    CustomerInfo,
    MyToolBox,
    ConversationReviewsList,
    CustomNotes,
    Campaign,
  },
  data() {
    return {
      sidePanelTabNames: SIDE_PANEL_TAB_NAMES,
      selectedCollapse: '',
      customerName: '',
      celebrationSettings: {},
    };
  },
  props: {
    videoPadding: {
      type: Boolean,
      default: false,
    },
    disable: {
      type: Boolean,
      default: false,
    },
    messages: {
      type: Array,
      default: [],
    },
  },
  emits: ['selectResponse'],
  async mounted() {
    this.$store.dispatch('tags/getTagsList', [{ limit: SIDE_PANEL_MAX_DROPBOX_ITEMS }]);
    this.$store.dispatch('categories/getCategoriesList', [{ limit: SIDE_PANEL_MAX_DROPBOX_ITEMS }]);
    this.$store.dispatch('teams/getTeamsList', [{ limit: SIDE_PANEL_MAX_DROPBOX_ITEMS }]);
    this.celebrationSettings = await this.$store.dispatch('settings/getSetting', {
      name: 'celebration',
    });
    const firstName = get(this.customer, 'profile.first_name', '');
    const lastName = get(this.customer, 'profile.last_name', '');
    this.customerName = `${firstName} ${lastName}`.trim();
    this.selectedCollapse = this.selectedCollapseTab;
  },
  watch: {
    conversation() {
      const firstName = get(this.customer, 'profile.first_name', '');
      const lastName = get(this.customer, 'profile.last_name', '');
      this.customerName = `${firstName} ${lastName}`.trim();
    },
    selectedCollapseTab(value, old) {
      if (value !== old) { this.selectedCollapse = value }
    },
  },
  computed: {
    ...mapGetters({
      teamsListState: 'teams/sortedTeams',
      isRealtimeContext: 'conversations/isRealtimeContext',
      me: 'agent/profile',
    }),
    ...mapState({
      conversation: state => state.conversations.selected,
      selectedCustomer: state => state.conversations.selectedCustomer,
      lastFeedbackInfo: state => state.conversations.selectedLastFeedbackInfo,
      notesPagination: state => state.conversations.notesPagination,
      schedules: state => state.conversations.schedules,
      tagsListState: state => state.tags.tags || [],
      categoriesListState: state => state.categories.categories || [],
      selectedCollapseTab: state => state.conversations.openedSidebarTab,
      tenantVariables: state => state.configs.tenantVariables,
      sidePanelConfig: state => state.configs.config.side_panel,
      FEATURE_FLAGS: state => state.featureFlags.FEATURE_FLAGS,
    }),
    customer() {
      return this.selectedCustomer || {};
    },
    showKnowledgeBase() {
      return Boolean(this.FEATURE_FLAGS.KNOWLEDGE_BASE);
    },
    categories() {
      return Array.of.apply(null, this.conversation.categories).sort(alphabetSort);
    },
    tags() {
      return Array.of.apply(null, this.conversation.tags).sort(alphabetSort);
    },
    teams() {
      return Array.of.apply(null, this.conversation.teams).sort(alphabetSort);
    },
    categoriesList() {
      return Array.of.apply(
        null, this.categoriesListState,
      )?.filter(
        e => !this.categories.find(j => j.id === e.id),
      ).sort(alphabetSort);
    },
    tagsList() {
      return this.tagsListState?.filter(e => !this.tags.find(j => j.id === e.id)).sort(alphabetSort);
    },
    teamsList() {
      return this.teamsListState?.filter(e => !this.teams.find(j => j.id === e.id));
    },
    profilesQrVariablesArray() {
      return [
        { profile: this.customer.profile, qrVariables: this.sidePanelConfig.profile_mapping },
        { profile: this.me, qrVariables: AGENT_PROFILE_MAPPING },
      ];
    },
    qrVariablesValuesMap() {
      const resultMap = new Map();
      for (const { profile, qrVariables } of this.profilesQrVariablesArray) {
        for (const variable of qrVariables) {
          const value = this.getProfileValue(variable, profile);
          if (variable.field) {
            resultMap.set(variable.field, value);
          }
          if (variable.label) {
            resultMap.set(variable.label, value);
          }
          // Special case to enable direct use of fields inside variables of type concat_string such as first_name
          if (variable.fields) {
            for (const f of variable.fields) {
              if (f.field) {
                const profileKey = f.field;
                const profileValue = variable.type === PROFILE_EDITOR_TYPE.CONCAT_STRING ? get(profile, profileKey, '') : value;
                resultMap.set(profileKey, profileValue);
              }
            }
          }
        }
      }
      return resultMap;
    },
    sidePanelItems() {
      return [
        {
          key: 'CUSTOMER_INFO',
          title: this.$t('conversation_tab.side_panel.customer_info_accordion.title'),
          shouldShow: true,
          iconName: 'address-card',
        },
        {
          key: 'NOTES',
          title: 'Notes',
          shouldShow: true,
          iconName: 'memo-pad',
          count: this.notesPagination?.rowCount || 0,
        },
        {
          key: 'REMINDERS',
          title: 'Reminders',
          shouldShow: true,
          iconName: 'calendar-clock',
        },
        {
          key: 'CONVERSATION_CLASSIFICATION',
          title: 'Conversation Classification',
          shouldShow: true,
          iconName: 'message-exclamation',
        },
        {
          key: 'HISTORY',
          title: 'History',
          shouldShow: true,
          iconName: 'clock-rotate-left',
        },
        {
          key: 'MY_TOOLBOX',
          title: 'Quick Responses',
          shouldShow: this.canView('/conversations/right_panel/quick_responses/mine') || this.canView('/conversations/right_panel/quick_responses/team'),
          iconName: 'reply-clock',
        },
        {
          key: 'KNOWLEDGE_BASE',
          title: 'Knowledge Base',
          shouldShow: this.showKnowledgeBase,
          iconName: 'book-open-cover',
        },
        {
          key: 'CONVERSATION_REVIEWS',
          title: 'Conversation Reviews',
          shouldShow: this.canView('/conversations/review_conversations'),
          iconName: 'message-check',
        },
        ...(this.FEATURE_FLAGS.SHOW_CAMPAIGN
          ? [
            {
              key: 'CAMPAIGN',
              title: 'Campaign',
              shouldShow: this.canView('/conversations/right_panel/view_campaign'),
              iconName: 'bullhorn',
            },
          ] : []),

      ];
    },
  },
  methods: {
    getProfileValue(qrVariable, profile) {
      const type = get(qrVariable, 'type', PROFILE_EDITOR_TYPE.STRING);
      if (type === PROFILE_EDITOR_TYPE.CONCAT_STRING) {
        const strs = [];
        for (const subQrVariable of get(qrVariable, 'fields', [])) {
          strs.push(get(profile, subQrVariable.field, ''));
        }
        return strs.join(' ').trim();
      }
      if (type === PROFILE_EDITOR_TYPE.STRING_LIST) {
        return (get(profile, qrVariable.field, []) || []).join(', ').trim();
      }
      return get(profile, qrVariable.field, '');
    },
    onSelectResponse(response) {
      // This regex matches patterns like #{variable} or #{variable|transformationFunction} in a string.
      // - #\{                     : Matches the opening sequence "#{"
      // - ([^|}]+)                : Captures the 'variable' (any characters except '|' or '}')
      // - (?:\|([^}]*))?          : Optionally captures the 'transformationFunction' after a '|' (if present), up to the closing '}'
      // - \}                      : Matches the closing brace
      // - g                       : Global flag to match all occurrences in the input
      const content = response.content;
      const metadata = response._quickResponseMetadata;

      let replacedContent = content.replaceAll(/#\{([^|}]+)(?:\|([^}]*))?\}/g, (match, variable, transformFn = '') => {
        let replacedValue = match;
        transformFn = transformFn.trim();
        variable = variable.trim();
        if (this.qrVariablesValuesMap.has(variable)) {
          replacedValue = this.qrVariablesValuesMap.get(variable);

          if (TRANSFORMATIONS_MAP.has(transformFn)) {
            const applyTransformation = TRANSFORMATIONS_MAP.get(transformFn);
            replacedValue = applyTransformation(replacedValue);
          }
        }
        return replacedValue;
      });
      replacedContent = replacedContent?.replace(/\n/g, '<br/>');

      const metadataWithConversation = metadata ? {
        ...metadata,
        attached_conversation_id: this.conversation.id,
      } : null;
      this.$emit('selectResponse', replacedContent, metadataWithConversation);
    },
    addTags(tags = []) {
      this.updateTags(tags.map(e => e.id), 'added');
    },
    createTag(name) {
      this.$store.dispatch('tags/createTag', [{ name }, 'data'])
        .then(data => {
          this.updateTags([...this.tags.map(e => e.id), data.data.id])
            .then(() => {
              this.$aiq.notify.info('New tag was created successfully.');
            });
        });
    },
    onTabClickChange(tabName) {
      if (!isEmpty(tabName)) {
        this.$store.dispatch(
          'notifications/emitEventToSocket',
          {
            name: `${SIDE_PANEL_PREFIX}.${tabName.toLowerCase()}.clicked`,
            payload: { agent: this.agent },
          },
        );
      }
      this.$store.dispatch('conversations/setSidePanelTab', tabName);
    },
    removeTag(id) {
      this.updateTags(this.tags?.filter(e => e.id !== id).map(e => e.id), 'removed');
    },
    updateTags(tags, updateType = 'updated') {
      return this.$store.dispatch('conversations/updateTags',
        [this.conversation.id,
          tags,
          updateType,
        ]);
    },
    addCategories(categories = []) {
      this.updateCategories(categories.map(e => e.id), 'added');
    },
    createCategory(category = '') {
      this.$store.dispatch('categories/createCategory', [{ name: category }, 'data'])
        .then(data => {
          this.updateCategories([...this.categories.map(e => e.id), data.data.id]).then(() => {
            this.$aiq.notify.info('Category was created successfully.');
          });
        });
    },
    removeCategory(id) {
      this.updateCategories(
        this.categories?.filter(e => e.id !== id).map(e => e.id),
        'removed',
      );
    },
    updateCategories(categories, updateType = 'updated') {
      return this.$store.dispatch('conversations/updateCategories',
        [this.conversation.id,
          categories,
          updateType,
        ]);
    },
    addTeams(teams = []) {
      this.updateTeams(teams.map(e => e.id), 'added');
    },
    createTeam(team = '') {
      this.$store.dispatch('teams/createTeam', [{ name: team }, 'data'])
        .then(data => {
          this.updateTeams([...this.teams.map(e => e.id), data.data.id])
            .then(() => {
              this.$aiq.notify.info('Team was created successfully.');
            });
        });
    },
    removeTeam(id) {
      this.updateTeams(
        this.teams?.filter(e => e.id !== id).map(e => e.id),
        'removed',
      );
    },
    updateTeams(teams, updateType = 'updated') {
      return this.$store.dispatch('conversations/updateTeams',
        [this.conversation.id, teams, updateType]);
    },
    saveCustomer() {
      const customerName = this.customerName.split(' ');
      const firstName = get(this.customer, 'profile.first_name', '');
      const lastName = get(this.customer, 'profile.last_name', '');
      const temporal = `${firstName} ${lastName}`.trim();
      if (this.customerName.trim() === temporal) return;
      const user = {
        last_name: '',
        fist_name: '',
      };
      const shortNameLength = 2;
      if (customerName.length >= shortNameLength) {
        user.last_name = customerName.pop();
      }
      user.first_name = customerName.join(' ');
      let newCustomer = this.customer.profile;
      newCustomer = Object.assign({ ...newCustomer }, user);
      this.updateProfile(newCustomer);
    },
    updateProfile(profile, id = this.customer.id) {
      this.$store.dispatch('conversations/updateCustomer', [
        id,
        { ...this.customer, profile },
      ]);
      this.$store.dispatch(
        'notifications/emitEventToSocket',
        {
          name: CUSTOMER_INFO.UPDATED,
          payload: { agent: this.agent, customer: { id, profile } },
        },
      );
    },
    async sendSystemMessage(action, content) {
      return await this.$store.dispatch('messages/sendSystemMessage', {
        conversation: pick(this.conversation, ['id']),
        action,
        content,
      });
    },
    async MuteToggled(muteMsg) {
      const action = muteMsg.isMuted ? 'mute_customer' : 'unmute_customer';
      return await this.sendSystemMessage(action, muteMsg.message);
    },
    returnSidePanelItem(key) {
      const sidePanelItem = this.sidePanelItems.find(item => item.key === key);
      return sidePanelItem || {};
    },
  },
};
